import React from 'react';
import styled, { css } from 'styled-components';
import { forDesktop, forMidWideScreens, forTablet, forWideScreens, isDesktop } from '@westwing/ui-kit/breakPoints';
import { AspectRatio } from '@westwing/ui-kit/AspectRatio';
import { CategorySlider } from '../../redux/homepage/types';
import Banner from '../Generic/Banner';
import { categorySliderRatios } from '../../constants/aspectRatios';
import HorizontalScrollableElement from '../ScrollableElement';
import useBellaTranslator from '../../hooks/useTranslator';
import { HorizontalScrollContainer } from '../ListingPage/Infusions';
import useGa from '../../hooks/useGa';
import { changePreformattedImageProperties } from '../../hooks/useImageFormatter';
import { BannerText } from './SecondaryBanner';
import useHomepageTracking from './useHomepageTracking';
import GenericImpressionWrapper from 'Client/components/Generic/GenericImpressionWrapper';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { BellaTestIds } from 'Common/qaIds';
import { sliderContainerStyles } from 'Client/components/Generic/ContentInfusionsSlider/SliderTile';

interface CategorySliderProps {
    categories: CategorySlider[];
}

export const CategorySliderStyledContainer = styled.div<{ p100IncreasedDesktopPageWidth?: boolean }>`
    ${sliderContainerStyles}

    ${({ p100IncreasedDesktopPageWidth }) => css`
        ${(p100IncreasedDesktopPageWidth ? forMidWideScreens : forWideScreens)`
            flex-basis: 303px;
            &:last-child {
                flex-basis: 303px;
                padding-right: 0;
            }
        `}
    `}
`;

const SliderWrapper = styled(HorizontalScrollableElement)`
    padding-left: 0;
    padding-top: 20px;
    margin-left: 40px;
    ${forDesktop`
        padding-top: 25px;
        margin: 0 20px;
    `}
`;

const StyledHorizontalScrollContainer = styled(HorizontalScrollContainer)`
    padding: 0 10px 0 40px;
    ${forTablet`
        padding: 0 0 0 40px;
    `}
    ${forDesktop`
        padding: 0;
    `}
`;

const StyledBannerText = styled(BannerText)`
    padding-top: 5px;
    max-width: 175px;
    ${forDesktop`
        max-width: 212px;
    `}
    ${forWideScreens`
        max-width: 303px;
    `}
`;

const StyledBanner = styled(Banner)`
    ${AspectRatio}
`;

const CategorySliders: React.FC<CategorySliderProps> = ({ categories = [] }) => {
    const t = useBellaTranslator();
    const { p100IncreasedDesktopPageWidth } = useFeatureToggle();
    const gaTracking = useGa();
    const { trackCategorySliderImpression } = useHomepageTracking();
    const slidertrackLabel = 'Category_slider';

    const addTracking = (
        cat: CategorySlider,
        i: number
    ): { banner: CategorySlider; trackingFn: (label: string) => void } => ({
        banner: cat,
        trackingFn: () => gaTracking.homepage.trackCategorySliderTrackingClick(cat.trackingName, i),
    });

    const trackArrowClicks = () => {
        if (isDesktop()) {
            gaTracking.homepage.trackSliderArrowComponentClick(slidertrackLabel);
        }
    };
    const handleBannerImpression = (trackingName: string) => {
        gaTracking.homepage.trackBannerImpression(trackingName);
    };

    const handleSliderImpression = () => {
        trackCategorySliderImpression();
        gaTracking.homepage.trackSliderImpression('Categories slider impression');
    };

    const categoriesWithTracking = categories.map(addTracking);

    return (
        <GenericImpressionWrapper action={handleSliderImpression}>
            <SliderWrapper
                title={t('homepage_topcategory')}
                arrowClickTrackFunction={trackArrowClicks}
                showHeader
                showNavArrows
                centerOffset={100}
            >
                <StyledHorizontalScrollContainer>
                    {categoriesWithTracking.map(({ banner, trackingFn }) => {
                        const formattedCmsForMobile = changePreformattedImageProperties(banner.image, {
                            height: '606',
                        });
                        return (
                            <CategorySliderStyledContainer
                                key={banner.title}
                                p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
                                data-testid={BellaTestIds.homepageCategorySlider}
                            >
                                <StyledBanner
                                    ratio={categorySliderRatios}
                                    desktopImage={banner.image}
                                    mobileImage={formattedCmsForMobile}
                                    targetUrl={banner.url}
                                    trackingFn={() => trackingFn(banner.title)}
                                    trackImpressionFunction={() => handleBannerImpression(banner.trackingName)}
                                    shouldLazyLoadBanner
                                />
                                <StyledBannerText {...banner} targetUrl={banner.url} trackingName="todo" />
                            </CategorySliderStyledContainer>
                        );
                    })}
                </StyledHorizontalScrollContainer>
            </SliderWrapper>
        </GenericImpressionWrapper>
    );
};

export default CategorySliders;
